'use client';

import {
  CheckCircleOutlined,
  CloseOutlined,
  CreditCardOutlined,
  EnvironmentOutlined,
  LeftOutlined,
  MinusOutlined,
  PhoneOutlined,
  PlusOutlined,
  RightOutlined,
  SafetyCertificateOutlined,
  ShoppingCartOutlined,
  StarFilled,
  TruckOutlined,
} from '@ant-design/icons';
import CustomNotification from '@root/src/components/common/CustomizeHTMComponent/notification';
import Image from '@components/common/ImageFallback';
import { DataContextCart } from '@root/src/contextcart';
import { convertCurrency } from '@root/src/lib/format';
import { fetchBannerProductDetail } from '@root/src/services/client/banner';
import productBannerDefault from '@src/assets/images/productbanner.jpg';
import Link from 'next/link';
import { useContext, useEffect, useMemo, useState } from 'react';
import MobileTwoColumnCarousel from '../common/MobileTwoColumnCarousel';
import { useTranslation } from '@root/src/i18n/client';
import { getProductsByCategorySlug } from '@root/src/services/client/category';
import { useRouter } from 'next/navigation';

const cleanArray = (value: any) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }
  return value ? [value] : [];
};

const normalizeText = (value?: string) => {
  if (!value) {
    return '';
  }
  const doc = new DOMParser().parseFromString(value, 'text/html');
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
};

const getFirstStockQuantity = (product: any) => {
  const firstStock =
    cleanArray(product?.dataStock)[0] || cleanArray(product?.quantity)[0];

  if (firstStock && typeof firstStock?.quantity !== 'undefined') {
    return Number(firstStock.quantity || 0);
  }

  return Number(product?.totalQuantity || product?.stock || 0);
};

const buildDescriptionLines = (product: any, selectedVariant: any, t: any) => {
  const attributeText = (selectedVariant?.productAttributes || [])
    .map((attribute: any) => attribute?.value)
    .filter(Boolean)
    .join(', ');

  return [
    attributeText &&
      t('lytra.productDetail.descFeature', {
        value: attributeText,
      }),
    product?.categoryName &&
      t('lytra.productDetail.descCategory', {
        value: product.categoryName,
      }),
    product?.weight && product?.unitweight
      ? t('lytra.productDetail.descWeight', {
          value: `${product.weight}${product.unitweight}`,
        })
      : '',
    product?.barcode &&
      t('lytra.productDetail.descBarcode', {
        value: product.barcode,
      }),
    product?.productKeyword &&
      t('lytra.productDetail.descFlavor', {
        value: product.productKeyword,
      }),
    product?.unit &&
      t('lytra.productDetail.descUnit', {
        value: product.unit,
      }),
  ].filter(Boolean);
};

const getVariantGroups = (variants: any[] = []) => {
  const groups = new Map<string, string[]>();

  variants.forEach(variant => {
    cleanArray(variant?.productAttributes).forEach((attribute: any) => {
      const name = attribute?.attributeName || 'variant-fallback';
      const value = attribute?.value;

      if (!value) {
        return;
      }

      const existingValues = groups.get(name) || [];
      if (!existingValues.includes(value)) {
        existingValues.push(value);
      }
      groups.set(name, existingValues);
    });
  });

  return Array.from(groups.entries()).map(([name, values]) => ({
    name,
    values,
  }));
};

const buildRecommendationLink = (item: any) =>
  item?.slug && item.slug !== '#' ? `/${item.slug}` : '/product';

const getProductImage = (product: any) =>
  cleanArray(product?.image)[0] || '/images/product.webp';

const getProductPrice = (product: any) =>
  Number(product?.retailPrice || product?.price || product?.variants?.[0]?.price || 0);

const normalizeRecommendationProduct = (product: any) => ({
  id: product?.id || product?.slug,
  name: product?.name || '',
  image: getProductImage(product),
  price: getProductPrice(product),
  slug: product?.slug || '#',
  discount: Number(product?.discount || 0),
  onPromotion: Boolean(product?.onPromotion),
  totalQuantity: getFirstStockQuantity(product),
});

const ProductDetails = ({ product }: { product?: any }) => {
  const { t } = useTranslation('common');
  const router = useRouter();
  const cartContext = useContext(DataContextCart);
  const dataCart = cartContext?.dataCart || [];
  const updateCart = cartContext?.updateCart;
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchEndX, setTouchEndX] = useState<number | null>(null);
  const [isImageZoomOpen, setIsImageZoomOpen] = useState(false);
  const [imageZoomScale, setImageZoomScale] = useState(1);
  const tabs = [
    { id: 'description', label: t('lytra.productDetail.tabDescription') },
    { id: 'brew', label: t('lytra.productDetail.tabBrew') },
  ];
  const uspItems = [
    {
      icon: CheckCircleOutlined,
      title: t('lytra.trustBadge1Title'),
      text: t('lytra.productDetail.usp1Desc'),
    },
    {
      icon: SafetyCertificateOutlined,
      title: t('lytra.productDetail.usp2Title'),
      text: t('lytra.productDetail.usp2Desc'),
    },
    {
      icon: TruckOutlined,
      title: t('lytra.trustBadge4Title'),
      text: t('lytra.trustBadge4Desc'),
    },
    {
      icon: CreditCardOutlined,
      title: t('lytra.productDetail.usp4Title'),
      text: t('lytra.productDetail.usp4Desc'),
    },
  ];
  const mobileUspItems = [...uspItems.slice(1), uspItems[0]];

  const variants = useMemo(() => cleanArray(product?.variants), [product?.variants]);
  const images = useMemo(() => cleanArray(product?.image), [product?.image]);
  const allImages = useMemo(
    () => (images.length > 0 ? images : ['/images/product.webp']),
    [images]
  );
  const variantGroups = useMemo(() => getVariantGroups(variants), [variants]);

  const [selectedImage, setSelectedImage] = useState(allImages[0]);
  const [activeTab, setActiveTab] = useState('description');
  const [qty, setQty] = useState(1);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationProductName, setNotificationProductName] = useState('');
  const [notificationQuantity, setNotificationQuantity] = useState(1);
  const [recommendations, setRecommendations] = useState<any[]>([]);
  const [productDetailBanner, setProductDetailBanner] = useState<any>(null);
  const [selectedAttributes, setSelectedAttributes] = useState<
    Record<string, string>
  >({});
  const categorySlugKey = useMemo(
    () => cleanArray(product?.categorySlug).join('|'),
    [product?.categorySlug]
  );

  useEffect(() => {
    const initialSelection: Record<string, string> = {};

    variantGroups.forEach(group => {
      const firstValue =
        variants
          .flatMap((variant: any) => cleanArray(variant?.productAttributes))
          .find((attribute: any) => attribute?.attributeName === group.name)
          ?.value || group.values[0];

      if (firstValue) {
        initialSelection[group.name] = firstValue;
      }
    });

    setSelectedAttributes(initialSelection);
  }, [variantGroups, variants]);

  useEffect(() => {
    const loadProductDetailBanner = async () => {
      try {
        const banner = await fetchBannerProductDetail();
        setProductDetailBanner(banner);
      } catch (error) {
        setProductDetailBanner(null);
      }
    };

    loadProductDetailBanner();
  }, []);

  const selectedVariant = useMemo(() => {
    if (!variants.length) {
      return undefined;
    }

    const matchedVariant = variants.find((variant: any) =>
        cleanArray(variant?.productAttributes).every((attribute: any) => {
        const attributeName = attribute?.attributeName || t('lytra.productDetail.variantFallback');
        return (
          !selectedAttributes[attributeName] ||
          selectedAttributes[attributeName] === attribute?.value
        );
      })
    );

    return matchedVariant || variants[0];
  }, [selectedAttributes, t, variants]);

  const currentImages = useMemo(() => {
    const variantImages = cleanArray(selectedVariant?.image);
    return variantImages.length > 0 ? variantImages : allImages;
  }, [allImages, selectedVariant]);

  useEffect(() => {
    setSelectedImage(currentImages[0]);
  }, [currentImages]);

  useEffect(() => {
    if (!isImageZoomOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsImageZoomOpen(false);
        setImageZoomScale(1);
      }

      if (event.key === 'ArrowLeft') {
        if (currentImages.length > 1) {
          const currentIndex = Math.max(
            currentImages.findIndex(image => image === selectedImage),
            0
          );
          const nextIndex = (currentIndex - 1 + currentImages.length) % currentImages.length;
          setSelectedImage(currentImages[nextIndex]);
        }
      }

      if (event.key === 'ArrowRight') {
        if (currentImages.length > 1) {
          const currentIndex = Math.max(
            currentImages.findIndex(image => image === selectedImage),
            0
          );
          const nextIndex = (currentIndex + 1) % currentImages.length;
          setSelectedImage(currentImages[nextIndex]);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isImageZoomOpen, currentImages, selectedImage]);

  useEffect(() => {
    let isMounted = true;

    const fetchRecommendations = async () => {
      const categorySlugs = cleanArray(product?.categorySlug).map(String);
      if (!categorySlugs.length) {
        setRecommendations([]);
        return;
      }

      const responses = await Promise.all(
        categorySlugs.map(slug => getProductsByCategorySlug(slug, 1, 10))
      );

      const currentId = product?.id ? String(product.id) : '';
      const currentSlug = product?.slug ? String(product.slug) : '';
      const uniqueProducts = new Map<string, any>();

      responses.forEach((response: any) => {
        const productList = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];

        productList.forEach((item: any) => {
          const itemId = item?.id ? String(item.id) : '';
          const itemSlug = item?.slug ? String(item.slug) : '';

          if (
            item?.status === false ||
            itemId === currentId ||
            itemSlug === currentSlug
          ) {
            return;
          }

          uniqueProducts.set(itemId || itemSlug, item);
        });
      });

      const nextRecommendations = Array.from(uniqueProducts.values())
        .slice(0, 5)
        .map(normalizeRecommendationProduct)
        .filter((item: any) => item.id && item.name);

      if (isMounted) {
        setRecommendations(nextRecommendations);
      }
    };

    fetchRecommendations();

    return () => {
      isMounted = false;
    };
  }, [product?.id, product?.slug, categorySlugKey]);

  if (!product) {
    return (
      <div className="px-4 py-16 text-center text-[#5f584e]">
        {t('lytra.productDetail.notFound')}
      </div>
    );
  }

  const retailPrice = Number(product?.retailPrice || selectedVariant?.price || 0);
  const isOnPromotion =
    Boolean(product?.onPromotion) && Number(product?.discount || 0) > 0;
  const finalPrice = isOnPromotion
    ? retailPrice - (retailPrice * Number(product?.discount || 0)) / 100
    : retailPrice;
  const totalStock = variants.length
    ? variants.reduce((sum: number, variant: any) => sum + Number(variant?.stock || 0), 0)
    : getFirstStockQuantity(product);
  const currentProductId = selectedVariant?.id || product?.id;
  const availableStock = Number(selectedVariant?.stock ?? totalStock ?? 0);
  const cartQuantity = Number(
    dataCart.find((item: any) => String(item.productId) === String(currentProductId))?.quantity || 0
  );
  const remainingStock = Math.max(availableStock - cartQuantity, 0);
  const isOutOfStock = availableStock <= 0;
  const isAtStockLimit = !isOutOfStock && remainingStock <= 0;
  const descriptionLines = buildDescriptionLines(product, selectedVariant, t);
  const selectedImageIndex = Math.max(
    currentImages.findIndex(image => image === selectedImage),
    0
  );

  useEffect(() => {
    if (remainingStock <= 0) {
      setQty(1);
      return;
    }

    setQty(current => Math.min(Math.max(1, current), remainingStock));
  }, [remainingStock, currentProductId]);

  const handleAttributeSelect = (groupName: string, value: string) => {
    setSelectedAttributes(current => ({
      ...current,
      [groupName]: value,
    }));
  };

  const handleImageStep = (direction: 'prev' | 'next') => {
    if (currentImages.length <= 1) {
      return;
    }

    const nextIndex =
      direction === 'next'
        ? (selectedImageIndex + 1) % currentImages.length
        : (selectedImageIndex - 1 + currentImages.length) % currentImages.length;

    setSelectedImage(currentImages[nextIndex]);
  };

  const handleOpenImageZoom = () => {
    setIsImageZoomOpen(true);
    setImageZoomScale(1);
  };

  const handleCloseImageZoom = () => {
    setIsImageZoomOpen(false);
    setImageZoomScale(1);
  };

  const handleZoomChange = (direction: 'in' | 'out') => {
    setImageZoomScale(current => {
      const nextScale = direction === 'in' ? current + 0.5 : current - 0.5;
      return Math.min(3, Math.max(1, nextScale));
    });
  };

  const handleImageTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    setTouchStartX(event.touches[0]?.clientX ?? null);
    setTouchEndX(null);
  };

  const handleImageTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    setTouchEndX(event.touches[0]?.clientX ?? null);
  };

  const handleImageTouchEnd = () => {
    if (touchStartX === null || touchEndX === null) {
      return;
    }

    const swipeDistance = touchStartX - touchEndX;
    const minSwipeDistance = 40;

    if (Math.abs(swipeDistance) < minSwipeDistance) {
      return;
    }

    handleImageStep(swipeDistance > 0 ? 'next' : 'prev');
    setTouchStartX(null);
    setTouchEndX(null);
  };

  const handleAddToCart = () => {
    if (!updateCart) {
      return;
    }
    if (isOutOfStock || isAtStockLimit || qty > remainingStock) {
      return;
    }

    const currentCart = [...dataCart];
    const productId = selectedVariant?.id || product?.id;
    const newProduct = {
      productId,
      productName: selectedVariant?.name
        ? `${product?.name} - ${selectedVariant.name}`
        : product?.name,
      productPrice: finalPrice,
      discount: Number(product?.discount || 0),
      productImage: selectedImage || currentImages[0],
      quantity: qty,
    };

    const existingIndex = currentCart.findIndex(
      item => item.productId === newProduct.productId
    );

    if (existingIndex >= 0) {
      currentCart[existingIndex].quantity = Math.min(
        currentCart[existingIndex].quantity + newProduct.quantity,
        availableStock
      );
    } else {
      currentCart.push(newProduct);
    }

    updateCart?.(currentCart);
    setNotificationProductName(product?.name || t('lytra.productFallbackName'));
    setNotificationQuantity(qty);
    setShowNotification(true);
  };

  const handleBuyNow = () => {
    if (isOutOfStock || isAtStockLimit || qty > remainingStock) {
      return;
    }

    const productId = selectedVariant?.id || product?.id;
    const buyNowItem = {
      productId,
      productName: selectedVariant?.name
        ? `${product?.name} - ${selectedVariant.name}`
        : product?.name,
      productPrice: finalPrice,
      discount: Number(product?.discount || 0),
      productImage: selectedImage || currentImages[0],
      quantity: qty,
    };

    localStorage.setItem('buyNowCart', JSON.stringify([buyNowItem]));
    router.push('/cart?mode=buy-now');
  };

  const handleAddRecommendationToCart = (item: any, event: any) => {
    event.preventDefault();
    event.stopPropagation();

    if (!updateCart) {
      return;
    }

    const currentCart = [...dataCart];
    const itemTotalQuantity = Number(item?.totalQuantity || 0);
    const newProduct = {
      productId: item.id,
      productName: item.name,
      productPrice: item.price,
      discount: item.onPromotion ? item.discount : 0,
      productImage: item.image,
      quantity: 1,
    };

    const existingIndex = currentCart.findIndex(
      cartItem => cartItem.productId === newProduct.productId
    );

    if (existingIndex >= 0) {
      if (itemTotalQuantity > 0 && currentCart[existingIndex].quantity >= itemTotalQuantity) {
        return;
      }
      currentCart[existingIndex].quantity += 1;
    } else {
      if (itemTotalQuantity <= 0) {
        return;
      }
      currentCart.push(newProduct);
    }

    updateCart(currentCart);
    setNotificationProductName(item.name);
    setNotificationQuantity(1);
    setShowNotification(true);
  };

  return (
    <div className="relative overflow-hidden bg-[#f8f2e8] py-8 text-[#3f3a32] lg:py-12">
      <div className="pointer-events-none absolute left-[-8rem] top-28 h-72 w-72 rounded-full bg-[#e7dcc9]/70 blur-3xl" />
      <div className="pointer-events-none absolute bottom-24 right-[-7rem] h-80 w-80 rounded-full bg-[#dfe4cf]/60 blur-3xl" />

      <div className="mx-auto max-w-[1280px] px-4 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1.05fr)_minmax(420px,0.95fr)]">
          <div className="space-y-5">
            <div className="overflow-hidden rounded-[32px] border border-[#e8dcc9] bg-[#fffaf4] shadow-[0_24px_60px_rgba(91,77,56,0.09)]">
              <div
                className="relative aspect-[1/1] touch-pan-y"
                onTouchStart={handleImageTouchStart}
                onTouchMove={handleImageTouchMove}
                onTouchEnd={handleImageTouchEnd}
              >
                {currentImages.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={() => handleImageStep('prev')}
                      className="absolute left-4 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-[#e6dac8] bg-white/90 text-[#5b5449] shadow-md transition hover:bg-white md:flex"
                    >
                      <LeftOutlined />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleImageStep('next')}
                      className="absolute right-4 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-[#e6dac8] bg-white/90 text-[#5b5449] shadow-md transition hover:bg-white md:flex"
                    >
                      <RightOutlined />
                    </button>
                  </>
                )}
                <Image
                  src={selectedImage || currentImages[0]}
                  alt={product?.name || t('lytra.productFallbackName')}
                  fill
                  priority
                  className="h-full w-full cursor-zoom-in object-cover"
                  onClick={handleOpenImageZoom}
                />
              </div>
            </div>

            <div className="grid grid-cols-4 gap-3 sm:grid-cols-5">
              {currentImages.slice(0, 5).map((image, index) => (
                <button
                  key={`${image}-${index}`}
                  type="button"
                  onClick={() => setSelectedImage(image)}
                  className={`overflow-hidden rounded-[18px] border bg-[#fffaf4] transition ${
                    selectedImage === image
                      ? 'border-[#68764b] shadow-[0_10px_20px_rgba(104,118,75,0.15)]'
                      : 'border-[#eadfce] hover:border-[#cbbd9e]'
                  }`}
                >
                  <div className="relative aspect-square">
                    <Image
                      src={image}
                      alt={`${product?.name || t('lytra.productFallbackName')} ${index + 1}`}
                      fill
                      className="h-full w-full object-cover"
                    />
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="flex h-full flex-col rounded-[34px] border border-[#ebdfcd] bg-[#fffaf4]/95 p-6 shadow-[0_24px_60px_rgba(91,77,56,0.08)] lg:p-8">
            <div className="space-y-4 border-b border-[#ece1d2] pb-6">
              <h1 className="font-serif text-[2rem] leading-none text-[#4d463b] sm:text-[2.3rem] lg:text-[2.7rem]">
                {product?.name}
              </h1>

              <div className="flex flex-wrap items-end gap-3 pt-2">
                <span className="text-[2.2rem] font-semibold text-[#4a4036]">
                  {convertCurrency(finalPrice)}
                </span>
                {isOnPromotion && (
                  <>
                    <span className="text-lg text-[#a69b8c] line-through">
                      {convertCurrency(retailPrice)}
                    </span>
                    <span className="rounded-full bg-[#f0e2c5] px-3 py-1 text-sm font-semibold text-[#8f6840]">
                      {t('lytra.productDetail.savePercent', { percent: product?.discount })}
                    </span>
                  </>
                )}
              </div>
            </div>

            <div className="space-y-6 py-6">
              {variantGroups.map(group => (
                <div key={group.name}>
                  <div className="mb-3 text-sm font-semibold text-[#5a5247]">
                    {group.name}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {group.values.map(value => {
                      const isActive = selectedAttributes[group.name] === value;
                      return (
                        <button
                          key={`${group.name}-${value}`}
                          type="button"
                          onClick={() => handleAttributeSelect(group.name, value)}
                          className={`min-w-[74px] rounded-[12px] border px-3 py-2 text-[13px] transition ${
                            isActive
                              ? 'border-[#667348] bg-[#667348] text-white'
                              : 'border-[#e4d8c8] bg-white text-[#5f5649] hover:border-[#bcae93]'
                          }`}
                        >
                          {value}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              {!variantGroups.length && (
                <div>
                  <div className="mb-3 text-sm font-semibold text-[#5a5247]">
                    {t('lytra.productDetail.weightLabel')}
                  </div>
                  <div className="inline-flex rounded-[12px] border border-[#667348] bg-[#667348] px-3 py-2 text-[13px] text-white">
                    {product?.weight && product?.unitweight
                      ? `${product.weight}${product.unitweight}`
                      : t('lytra.productDetail.standardLabel')}
                  </div>
                </div>
              )}

              <div>
                <div className="mb-3 text-sm font-semibold text-[#5a5247]">
                  {t('Quantity')}
                </div>
                <div className="flex flex-wrap items-center gap-4">
                  <div className="inline-flex items-center rounded-[12px] border border-[#e5dac9] bg-white">
                    <button
                      type="button"
                      onClick={() => setQty(current => Math.max(1, current - 1))}
                      className="flex h-10 w-10 items-center justify-center text-[#5a5247] transition hover:bg-[#f5efe5]"
                    >
                      <MinusOutlined />
                    </button>
                    <div className="flex h-10 min-w-[52px] items-center justify-center border-x border-[#ece1d2] px-3 font-medium text-[#4d463b]">
                      {qty}
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setQty(current => Math.min(remainingStock || 1, current + 1))
                      }
                      disabled={isOutOfStock || isAtStockLimit || qty >= remainingStock}
                      className="flex h-10 w-10 items-center justify-center text-[#5a5247] transition hover:bg-[#f5efe5] disabled:cursor-not-allowed disabled:text-[#c6bcae] disabled:hover:bg-transparent"
                    >
                      <PlusOutlined />
                    </button>
                  </div>
                  <div className={`text-sm ${isOutOfStock ? 'text-[#a14b4b]' : 'text-[#7c7366]'}`}>
                    {isOutOfStock
                      ? 'Liên hệ 0931 338 566'
                      : isAtStockLimit
                        ? `Đã thêm tối đa: ${availableStock}`
                        : `Còn lại: ${remainingStock}`}
                  </div>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={handleAddToCart}
                  disabled={isOutOfStock || isAtStockLimit}
                  className="flex items-center justify-center gap-2 rounded-[14px] bg-[#667348] px-4 py-3 text-[13px] font-semibold uppercase tracking-[0.05em] text-white transition hover:bg-[#56603c] disabled:cursor-not-allowed disabled:bg-[#b8b29f] disabled:hover:bg-[#b8b29f]"
                >
                  <ShoppingCartOutlined />
                  {t('addtocart')}
                </button>
                <button
                  type="button"
                  onClick={handleBuyNow}
                  disabled={isOutOfStock || isAtStockLimit}
                  className="rounded-[14px] border border-[#cfbfaa] bg-[#fffaf4] px-4 py-3 text-[13px] font-semibold uppercase tracking-[0.05em] text-[#5a5247] transition hover:border-[#667348] hover:text-[#667348] disabled:cursor-not-allowed disabled:border-[#ddd4c7] disabled:text-[#b8b09f] disabled:hover:border-[#ddd4c7] disabled:hover:text-[#b8b09f]"
                >
                  {t('lytra.productDetail.buyNow')}
                </button>
              </div>

              <CustomNotification
                message={t('lytra.productDetail.addedToCart', {
                  count: notificationQuantity,
                  name:
                    notificationProductName ||
                    product?.name ||
                    t('lytra.productFallbackName'),
                })}
                show={showNotification}
                onClose={() => setShowNotification(false)}
                bgColor="bg-[#667348]"
                textColor="text-white"
              />
            </div>

            <div className="hidden gap-4 border-t border-[#ece1d2] pt-6 md:grid md:sm:grid-cols-2">
              <div className="flex items-start gap-3 text-sm text-[#685f53]">
                <PhoneOutlined className="mt-1 text-[#667348]" />
                <div>
                  <div className="font-semibold text-[#4f483f]">{t('lytra.productDetail.orderConsulting')}</div>
                  <div>0931 338 566</div>
                </div>
              </div>
              <div className="flex items-start gap-3 text-sm text-[#685f53]">
                <EnvironmentOutlined className="mt-1 text-[#667348]" />
                <div>
                  <div className="font-semibold text-[#4f483f]">{t('store')}</div>
                  <div>{t('AddressContactss')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <section className="hidden">
          <div className="mb-6 flex items-end justify-between gap-4">
            <div>
              <div className="font-serif text-[2rem] text-[#4f483f]">
                {t('lytra.productDetail.mayLikeTitle')}
              </div>
              <div className="mt-2 text-sm text-[#857b6d]">
                {t('lytra.productDetail.mayLikeDesc')}
              </div>
            </div>
            <Link
              href="/product"
              className="text-sm font-semibold text-[#667348] underline-offset-4 transition hover:text-[#556039] hover:underline"
            >
              {t('lytra.viewAllProducts')}
            </Link>
          </div>

          <MobileTwoColumnCarousel
            items={recommendations}
            renderItem={item => (
              <Link
                key={item.id}
                href={buildRecommendationLink(item)}
                className="overflow-hidden rounded-[24px] border border-[#eadfce] bg-[#fffaf4] shadow-[0_14px_30px_rgba(91,77,56,0.05)] transition hover:-translate-y-1"
              >
                <div className="relative aspect-[0.9]">
                  <Image
                    src={item.image}
                    alt={item.name}
                    fill
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="p-4">
                  <div className="min-h-[48px] text-[15px] font-medium text-[#4e473d]">
                    {item.name}
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <span className="text-base font-semibold text-[#4c4438]">
                      {convertCurrency(item.price)}
                    </span>
                    <button
                      type="button"
                      onClick={event => handleAddRecommendationToCart(item, event)}
                      disabled={Number(item?.totalQuantity || 0) <= 0}
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-[#d8ccb8] text-[#667348] transition hover:border-[#667348] hover:bg-[#667348] hover:text-white disabled:cursor-not-allowed disabled:border-[#ddd4c7] disabled:text-[#b8b09f] disabled:hover:bg-transparent disabled:hover:text-[#b8b09f] disabled:hover:border-[#ddd4c7]"
                    >
                      <ShoppingCartOutlined />
                    </button>
                  </div>
                </div>
              </Link>
            )}
          />
        </section>

        <div className="mt-8 hidden rounded-[30px] border border-[#ebdfcd] bg-[#fffaf4]/95 px-5 py-6 md:block lg:px-8">
          <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
            {uspItems.map(item => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="flex items-start gap-4">
                  <div className="flex h-8 w-8 items-center justify-center text-[22px] text-[#667348]">
                    <Icon />
                  </div>
                  <div>
                    <div className="font-semibold text-[#50483d]">{item.title}</div>
                    <p className="mt-1 text-sm leading-6 text-[#766d60]">
                      {item.text}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="hidden">
          <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
            {mobileUspItems.map(item => {
              const Icon = item.icon;
              return (
                <div key={`mobile-${item.title}`} className="flex items-start gap-4">
                  <div className="flex h-8 w-8 items-center justify-center text-[22px] text-[#667348]">
                    <Icon />
                  </div>
                  <div>
                    <div className="font-semibold text-[#50483d]">{item.title}</div>
                    <p className="mt-1 text-sm leading-6 text-[#766d60]">
                      {item.text}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 grid gap-4 border-t border-[#ece1d2] pt-6">
            <div className="flex items-start gap-3 text-sm text-[#685f53]">
              <PhoneOutlined className="mt-1 text-[#667348]" />
              <div>
                <div className="font-semibold text-[#4f483f]">
                  {t('lytra.productDetail.orderConsulting')}
                </div>
                <div>0931 338 566</div>
              </div>
            </div>
            <div className="flex items-start gap-3 text-sm text-[#685f53]">
              <EnvironmentOutlined className="mt-1 text-[#667348]" />
              <div>
                <div className="font-semibold text-[#4f483f]">{t('store')}</div>
                <div>{t('AddressContactss')}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)]">
          <div className="rounded-[30px] border border-[#ebdfcd] bg-[#fffaf4]/95 p-6 shadow-[0_16px_40px_rgba(91,77,56,0.06)] lg:p-8">
            <div className="flex flex-wrap gap-6 border-b border-[#ece1d2]">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`border-b-2 pb-4 text-sm font-semibold uppercase tracking-[0.08em] transition ${
                    activeTab === tab.id
                      ? 'border-[#667348] text-[#4d463b]'
                      : 'border-transparent text-[#857b6d] hover:text-[#5e574c]'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'description' && (
              <div className="pt-6">
                <p className="mb-5 text-[15px] leading-8 text-[#675f53]">
                  {normalizeText(product?.description) ||
                    t('lytra.productDetail.descriptionFallback')}
                </p>
                <div className="space-y-3 text-[15px] leading-7 text-[#5e574d]">
                  {descriptionLines.map(line => (
                    <div key={line} className="flex gap-3">
                      <span className="mt-[7px] h-2 w-2 rounded-full bg-[#889369]" />
                      <span>{line}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'brew' && (
              <div className="pt-6 text-[15px] leading-8 text-[#5f584d] max-h-[400px] overflow-y-auto">
                {product?.descriptionContent ? (
                  <div
                    className="[&_img]:max-w-full [&_img]:h-auto [&_table]:w-full [&_td]:border [&_td]:border-[#e4d8c8] [&_td]:p-2 [&_th]:border [&_th]:border-[#e4d8c8] [&_th]:p-2 [&_th]:bg-[#f5efe5]"
                    dangerouslySetInnerHTML={{ __html: product.descriptionContent }}
                  />
                ) : (
                  <>
                    <p>{t('lytra.productDetail.brew1')}</p>
                    <p>{t('lytra.productDetail.brew2')}</p>
                    <p>{t('lytra.productDetail.brew3')}</p>
                    <p>{t('lytra.productDetail.brew4')}</p>
                  </>
                )}
              </div>
            )}

          </div>

          <Link
            href="/product"
            className="relative block overflow-hidden rounded-[30px] border border-[#e7d9c4] shadow-[0_16px_40px_rgba(91,77,56,0.06)] transition hover:opacity-95"
          >
            <div className="relative aspect-[1.15] min-h-[280px]">
              <Image
                src={productDetailBanner?.imagePath || productBannerDefault}
                alt={`${product?.name || t('lytra.productFallbackName')} promo`}
                fill
                className="h-full w-full object-cover object-left"
              />
            </div>
          </Link>
        </div>

        <section className="mt-10 md:hidden">
          <div className="mb-6 flex items-end justify-between gap-4">
            <div className="min-w-0">
              <div className="font-serif text-[1.6rem] sm:text-[2rem] text-[#4f483f] whitespace-nowrap">
                {t('lytra.productDetail.mayLikeTitle')}
              </div>
              <div className="mt-2 text-sm text-[#857b6d]">
                {t('lytra.productDetail.mayLikeDesc')}
              </div>
            </div>
            <Link
              href="/product"
              className="text-sm font-semibold text-[#667348] underline-offset-4 transition hover:text-[#556039] hover:underline"
            >
              {t('lytra.viewAllProducts')}
            </Link>
          </div>

          <MobileTwoColumnCarousel
            items={recommendations}
            renderItem={item => (
              <Link
                key={item.id}
                href={buildRecommendationLink(item)}
                className="overflow-hidden rounded-[24px] border border-[#eadfce] bg-[#fffaf4] shadow-[0_14px_30px_rgba(91,77,56,0.05)] transition hover:-translate-y-1"
              >
                <div className="relative aspect-[0.9]">
                  <Image
                    src={item.image}
                    alt={item.name}
                    fill
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="p-4">
                  <div className="min-h-[48px] text-[15px] font-medium text-[#4e473d]">
                    {item.name}
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <span className="text-base font-semibold text-[#4c4438]">
                      {convertCurrency(item.price)}
                    </span>
                    <button
                      type="button"
                      onClick={event => handleAddRecommendationToCart(item, event)}
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-[#d8ccb8] text-[#667348] transition hover:border-[#667348] hover:bg-[#667348] hover:text-white"
                    >
                      <ShoppingCartOutlined />
                    </button>
                  </div>
                </div>
              </Link>
            )}
          />
        </section>

        <div className="mt-8 rounded-[30px] border border-[#ebdfcd] bg-[#fffaf4]/95 px-5 py-6 md:hidden">
          <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
            {mobileUspItems.map(item => {
              const Icon = item.icon;
              return (
                <div key={`mobile-bottom-${item.title}`} className="flex items-start gap-4">
                  <div className="flex h-8 w-8 items-center justify-center text-[22px] text-[#667348]">
                    <Icon />
                  </div>
                  <div>
                    <div className="font-semibold text-[#50483d]">{item.title}</div>
                    <p className="mt-1 text-sm leading-6 text-[#766d60]">
                      {item.text}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 grid gap-4 border-t border-[#ece1d2] pt-6">
            <div className="flex items-start gap-3 text-sm text-[#685f53]">
              <PhoneOutlined className="mt-1 text-[#667348]" />
              <div>
                <div className="font-semibold text-[#4f483f]">
                  {t('lytra.productDetail.orderConsulting')}
                </div>
                <div>0931 338 566</div>
              </div>
            </div>
            <div className="flex items-start gap-3 text-sm text-[#685f53]">
              <EnvironmentOutlined className="mt-1 text-[#667348]" />
              <div>
                <div className="font-semibold text-[#4f483f]">{t('store')}</div>
                <div>{t('AddressContactss')}</div>
              </div>
            </div>
          </div>
        </div>

        <section className="mt-12 hidden md:block">
          <div className="mb-6 flex items-end justify-between gap-4">
            <div className="min-w-0">
              <div className="font-serif text-[2rem] text-[#4f483f] whitespace-nowrap">
                {t('lytra.productDetail.mayLikeTitle')}
              </div>
              <div className="mt-2 text-sm text-[#857b6d]">
                {t('lytra.productDetail.mayLikeDesc')}
              </div>
            </div>
            <Link
              href="/product"
              className="text-sm font-semibold text-[#667348] underline-offset-4 transition hover:text-[#556039] hover:underline"
            >
              {t('lytra.viewAllProducts')}
            </Link>
          </div>

          <div className="hidden gap-5 sm:grid sm:grid-cols-2 lg:grid-cols-5">
            {recommendations.map(item => (
              <Link
                key={item.id}
                href={buildRecommendationLink(item)}
                className="overflow-hidden rounded-[24px] border border-[#eadfce] bg-[#fffaf4] shadow-[0_14px_30px_rgba(91,77,56,0.05)] transition hover:-translate-y-1"
              >
                <div className="relative aspect-[0.9]">
                  <Image
                    src={item.image}
                    alt={item.name}
                    fill
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="p-4">
                  <div className="min-h-[48px] text-[15px] font-medium text-[#4e473d]">
                    {item.name}
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <span className="text-base font-semibold text-[#4c4438]">
                      {convertCurrency(item.price)}
                    </span>
                    <button
                      type="button"
                      onClick={event => handleAddRecommendationToCart(item, event)}
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-[#d8ccb8] text-[#667348] transition hover:border-[#667348] hover:bg-[#667348] hover:text-white"
                    >
                      <ShoppingCartOutlined />
                    </button>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <MobileTwoColumnCarousel
            items={recommendations}
            renderItem={item => (
              <Link
                key={item.id}
                href={buildRecommendationLink(item)}
                className="overflow-hidden rounded-[24px] border border-[#eadfce] bg-[#fffaf4] shadow-[0_14px_30px_rgba(91,77,56,0.05)] transition hover:-translate-y-1"
              >
                <div className="relative aspect-[0.9]">
                  <Image
                    src={item.image}
                    alt={item.name}
                    fill
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="p-4">
                  <div className="min-h-[48px] text-[15px] font-medium text-[#4e473d]">
                    {item.name}
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <span className="text-base font-semibold text-[#4c4438]">
                      {convertCurrency(item.price)}
                    </span>
                    <button
                      type="button"
                      onClick={event => handleAddRecommendationToCart(item, event)}
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-[#d8ccb8] text-[#667348] transition hover:border-[#667348] hover:bg-[#667348] hover:text-white"
                    >
                      <ShoppingCartOutlined />
                    </button>
                  </div>
                </div>
              </Link>
            )}
          />
        </section>

        {isImageZoomOpen && (
          <div
            className="fixed inset-0 z-[120] flex items-center justify-center bg-[#201912]/85 px-4 py-6 backdrop-blur-sm"
            onClick={handleCloseImageZoom}
          >
            <div
              className="relative flex h-full max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-[28px] border border-white/15 bg-[#f8f2e8]"
              onClick={event => event.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-[#e5d8c5] px-4 py-3 sm:px-6">
                <div className="text-sm font-medium text-[#5b5449]">
                  {selectedImageIndex + 1}/{currentImages.length}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleZoomChange('out')}
                    disabled={imageZoomScale <= 1}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-[#dbcdb7] bg-white text-[#5b5449] transition hover:border-[#bba98a] disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label="Zoom out"
                  >
                    <MinusOutlined />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleZoomChange('in')}
                    disabled={imageZoomScale >= 3}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-[#dbcdb7] bg-white text-[#5b5449] transition hover:border-[#bba98a] disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label="Zoom in"
                  >
                    <PlusOutlined />
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseImageZoom}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-[#dbcdb7] bg-white text-[#5b5449] transition hover:border-[#bba98a]"
                    aria-label="Close image preview"
                  >
                    <CloseOutlined />
                  </button>
                </div>
              </div>

              <div className="relative flex-1 overflow-auto bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.6),_rgba(232,220,201,0.95))]">
                {currentImages.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={() => handleImageStep('prev')}
                      className="absolute left-4 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-[#dbcdb7] bg-white/95 text-[#5b5449] shadow-md transition hover:bg-white"
                      aria-label="Previous image"
                    >
                      <LeftOutlined />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleImageStep('next')}
                      className="absolute right-4 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-[#dbcdb7] bg-white/95 text-[#5b5449] shadow-md transition hover:bg-white"
                      aria-label="Next image"
                    >
                      <RightOutlined />
                    </button>
                  </>
                )}

                <div className="flex min-h-full min-w-full items-center justify-center p-6 sm:p-10">
                  <div
                    className="relative h-[55vh] w-full max-w-5xl transition-transform duration-200 ease-out sm:h-[72vh]"
                    style={{ transform: `scale(${imageZoomScale})` }}
                  >
                    <Image
                      src={selectedImage || currentImages[0]}
                      alt={product?.name || t('lytra.productFallbackName')}
                      fill
                      priority
                      className="h-full w-full object-contain"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetails;
